import { useCallback, useEffect, useRef, useState } from 'react';
import { isBridgeIdentifier } from '@oysterjs/smartfill-contracts';
import { useSidebarSession } from '../bridge/session';
import { createOperationId } from './presentation';
import { displayError, type DisplayError } from '../bridge/errors';

export type ConversionStatus = 'idle' | 'loading' | 'error';

export interface WorkspaceConversionState {
  operationId: string | null;
  status: ConversionStatus;
  error: DisplayError | null;
}

const idleConversion: WorkspaceConversionState = {
  operationId: null,
  status: 'idle',
  error: null
};

const WORKSPACE_CONVERSION_REQUEST_TIMEOUT_MS = 30_000;
const WORKSPACE_CONVERSION_POLL_INTERVAL_MS = 1_000;

const WORKSPACE_CONVERSION_STORAGE_KEY = 'cara-smart-fill-workspace-conversions-v1';

function loadWorkspaceConversionStates(): Record<string, WorkspaceConversionState> {
  try {
    const stored = window.localStorage.getItem(WORKSPACE_CONVERSION_STORAGE_KEY);
    if (!stored) return {};
    const parsed: unknown = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const states: Record<string, WorkspaceConversionState> = {};
    for (const [workspaceId, operationId] of Object.entries(parsed)) {
      if (!isBridgeIdentifier(workspaceId) || !isBridgeIdentifier(operationId)) continue;
      states[workspaceId] = { operationId, status: 'loading', error: null };
    }
    return states;
  } catch {
    return {};
  }
}

function persistWorkspaceConversionStates(states: Record<string, WorkspaceConversionState>): void {
  const activeOperations = Object.fromEntries(
    Object.entries(states).flatMap(([workspaceId, state]) =>
      state.operationId ? [[workspaceId, state.operationId]] : []
    )
  );
  try {
    if (Object.keys(activeOperations).length === 0) {
      window.localStorage.removeItem(WORKSPACE_CONVERSION_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(WORKSPACE_CONVERSION_STORAGE_KEY, JSON.stringify(activeOperations));
  } catch {
    // The conversion still runs server-side when durable browser storage is unavailable.
  }
}

function waitForWorkspaceConversionPoll(): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, WORKSPACE_CONVERSION_POLL_INTERVAL_MS);
  });
}

export function useWorkspaceConversion() {
  const { client } = useSidebarSession();
  const [states, setStates] = useState<Record<string, WorkspaceConversionState>>(
    loadWorkspaceConversionStates
  );
  const statesRef = useRef(states);
  const inFlight = useRef(new Set<string>());
  const mounted = useRef(true);

  useEffect(() => {
    statesRef.current = states;
  }, [states]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const update = useCallback((workspaceId: string, next: WorkspaceConversionState) => {
    if (!mounted.current) return;
    statesRef.current = { ...statesRef.current, [workspaceId]: next };
    persistWorkspaceConversionStates(statesRef.current);
    setStates(statesRef.current);
  }, []);

  const convert = useCallback(
    async (workspaceId: string): Promise<string[] | null> => {
      if (inFlight.current.has(workspaceId)) return null;
      inFlight.current.add(workspaceId);
      const current = statesRef.current[workspaceId] ?? idleConversion;
      const operationId = current.operationId ?? createOperationId();
      update(workspaceId, { operationId, status: 'loading', error: null });
      try {
        while (mounted.current) {
          const conversion = await client.request(
            'workspaces.convert',
            {
              workspaceId,
              operationId
            },
            { timeoutMs: WORKSPACE_CONVERSION_REQUEST_TIMEOUT_MS }
          );
          if (conversion.completed) {
            update(workspaceId, { operationId: null, status: 'idle', error: null });
            return conversion.bucketIds;
          }
          await waitForWorkspaceConversionPoll();
        }
        return null;
      } catch (requestError) {
        const error = displayError(requestError, 'Could not generate buckets from this workspace.');
        const shouldResume =
          error.code === 'NETWORK' || error.code === 'TIMEOUT' || error.code === 'DISCONNECTED';
        update(workspaceId, {
          operationId: shouldResume ? operationId : null,
          status: 'error',
          error
        });
        return null;
      } finally {
        inFlight.current.delete(workspaceId);
      }
    },
    [client, update]
  );

  return {
    convert,
    stateFor: (workspaceId: string) => states[workspaceId] ?? idleConversion,
    activeWorkspaceIds: Object.entries(states).flatMap(([workspaceId, state]) =>
      state.status === 'loading' && state.operationId ? [workspaceId] : []
    )
  };
}
